import { Button } from '@/components/ui/button';
import { BookOpen, Sparkles, TrendingUp, CheckCircle, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSaveToTBR } from '@/hooks/useSaveToTBR';
import { toast } from 'sonner';
import { CoverImage } from './CoverImage';
import { useRecommendationFeedback } from '@/hooks/useRecommendationFeedback';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { QuickReviewDialog } from './QuickReviewDialog';
import { ReadingFormat } from '@/lib/readingFormatUtils';
import { submitReview } from '@/hooks/useReviewSubmit';

interface BookRecommendationCardProps {
  title: string;
  author: string;
  reason?: string;
  bookId?: string;
  isbn13?: string;
  isbn10?: string;
  coverUrl?: string;
  confidenceScore?: number; // 0-100
  matchDetails?: string; // Specific examples from user's history
  sourceBooks?: Array<{
    id: string;
    title: string;
    reason: string;
  }>;
  recommendationType?: 'safe_bet' | 'stretch' | 'wildcard' | 'classic';
  onDismiss?: (bookId?: string) => void;
  compact?: boolean; // Smaller card for chat interface
}

export function BookRecommendationCard({ 
  title, 
  author,
  reason,
  bookId,
  isbn13,
  isbn10,
  coverUrl,
  confidenceScore,
  matchDetails,
  sourceBooks,
  recommendationType,
  onDismiss,
  compact = false
}: BookRecommendationCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toggleSave, loading: isSaving, isSaved } = useSaveToTBR(
    bookId || '', 
    undefined, 
    { title, author, isbn13, isbn10 }
  );
  const { submitFeedback, loading: feedbackLoading } = useRecommendationFeedback();
  const [dismissed, setDismissed] = useState(false);
  const [reasoningDialogOpen, setReasoningDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewBookId, setReviewBookId] = useState<string | undefined>(bookId);
  const [isInLibrary, setIsInLibrary] = useState(false);
  const [libraryShelf, setLibraryShelf] = useState<string | null>(null);

  // Check if book is already in user's library by ISBN or bookId
  useEffect(() => {
    const checkLibraryStatus = async () => {
      if (!user) return;
      
      try {
        // Build OR query for bookId and ISBNs
        let query = supabase
          .from('user_books')
          .select('shelf, book_id, books!inner(isbn13, isbn10)')
          .eq('user_id', user.id);
        
        if (bookId) {
          const { data } = await query.eq('book_id', bookId).maybeSingle();
          if (data) {
            setIsInLibrary(true);
            setLibraryShelf(data.shelf);
            return;
          }
        }
        
        // Check by ISBN if no bookId match
        if (isbn13 || isbn10) {
          const { data: allBooks } = await supabase
            .from('user_books')
            .select('shelf, books!inner(isbn13, isbn10)')
            .eq('user_id', user.id);
          
          if (allBooks) {
            const match = allBooks.find((ub: any) => 
              (isbn13 && ub.books?.isbn13 === isbn13) || 
              (isbn10 && ub.books?.isbn10 === isbn10)
            );
            if (match) {
              setIsInLibrary(true);
              setLibraryShelf(match.shelf);
            }
          }
        }
      } catch (err) {
        console.error('Error checking library status:', err);
      }
    };
    
    checkLibraryStatus();
  }, [user, bookId, isbn13, isbn10]);

  // Combine the hook's isSaved with our library check
  const bookIsSaved = isSaved || (isInLibrary && libraryShelf === 'to-read');

  const handleAddToTBR = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    await toggleSave();
    
    // Log upvote feedback when adding to TBR
    await submitFeedback({
      bookId,
      bookTitle: title,
      bookAuthor: author,
      isbn13,
      isbn10,
      feedbackType: 'upvote',
      recommendationReason: reason,
    });
  };

  const handleViewDetails = async () => {
    try {
      if (bookId) {
        navigate(`/book/${bookId}`);
        return;
      }

      // Try to find existing book by ISBNs
      if (isbn13 || isbn10) {
        const orFilters = [
          isbn13 ? `isbn13.eq.${isbn13}` : null,
          isbn10 ? `isbn10.eq.${isbn10}` : null,
        ].filter(Boolean).join(',');

        const { data: existing, error: findError } = await supabase
          .from('books')
          .select('id')
          .or(orFilters)
          .limit(1)
          .maybeSingle();

        if (findError) throw findError;
        if (existing?.id) {
          navigate(`/book/${existing.id}`);
          return;
        }
      }

      // Create minimal book record then navigate
      const { data: created, error: insertError } = await supabase
        .from('books')
        .insert({
          title,
          authors: author ? [author] : [],
          isbn13: isbn13 || null,
          isbn10: isbn10 || null,
          cover_url: coverUrl || null,
          source: 'openlibrary',
          language: 'en',
          enrichment_status: 'pending',
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      navigate(`/book/${created.id}`);
    } catch (err) {
      console.error('Error opening book from rec card', err);
      toast.error('Unable to open book', {
        description: 'Please try again in a moment.',
      });
    }
  };

  const handleDismiss = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Submit downvote feedback and dismiss
    const result = await submitFeedback({
      bookId,
      bookTitle: title,
      bookAuthor: author,
      isbn13,
      isbn10,
      feedbackType: 'downvote',
      recommendationReason: reason,
    });

    if (result.success) {
      setDismissed(true);
      onDismiss?.(bookId);
    }
  };

  if (dismissed) {
    return null;
  }

  const handleAlreadyRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      let targetBookId = bookId;
      
      // If no bookId, try to find existing book by ISBNs
      if (!targetBookId && (isbn13 || isbn10)) {
        const orFilters = [
          isbn13 ? `isbn13.eq.${isbn13}` : null,
          isbn10 ? `isbn10.eq.${isbn10}` : null,
        ].filter(Boolean).join(',');

        const { data: existing, error: findError } = await supabase
          .from('books')
          .select('id')
          .or(orFilters)
          .limit(1)
          .maybeSingle();

        if (findError) throw findError;
        if (existing?.id) {
          targetBookId = existing.id;
        }
      }

      // Check if user already reviewed this book - try multiple approaches
      if (user) {
        let existingReview = null;
        
        // First try by bookId if we have one
        if (targetBookId) {
          const { data } = await supabase
            .from('user_books')
            .select('rating_star, review_text, book_id')
            .eq('user_id', user.id)
            .eq('book_id', targetBookId)
            .maybeSingle();
          existingReview = data;
        }
        
        // If not found and we have title+author, check by title similarity
        if (!existingReview && title && author) {
          const normalizedTitle = title.toLowerCase().replace(/[^a-z0-9]/g, '');
          const { data: userBooks } = await supabase
            .from('user_books')
            .select('rating_star, review_text, book_id, books:books!user_books_book_id_fkey(title, authors)')
            .eq('user_id', user.id)
            .not('rating_star', 'is', null);
          
          if (userBooks) {
            for (const ub of userBooks) {
              const ubTitle = (ub.books?.title || '').toLowerCase().replace(/[^a-z0-9]/g, '');
              const ubAuthor = (ub.books?.authors?.[0] || '').toLowerCase();
              if (ubTitle.includes(normalizedTitle) || normalizedTitle.includes(ubTitle)) {
                if (ubAuthor.includes(author.toLowerCase().split(' ')[0])) {
                  existingReview = ub;
                  targetBookId = ub.book_id;
                  break;
                }
              }
            }
          }
        }

        // If already rated or reviewed, show toast and dismiss the card
        if (existingReview && (existingReview.rating_star || existingReview.review_text)) {
          toast.success('Already reviewed!', {
            description: `You've already rated "${title}". Removing from recommendations.`,
          });
          
          // Submit positive feedback since they've read it
          await submitFeedback({
            bookId: targetBookId || '',
            bookTitle: title,
            bookAuthor: author,
            isbn13,
            isbn10,
            feedbackType: 'upvote',
            recommendationReason: reason,
          });
          
          setDismissed(true);
          onDismiss?.();
          return;
        }
      }

      // If no bookId yet, create minimal book record
      if (!targetBookId) {
        const { data: created, error: insertError } = await supabase
          .from('books')
          .insert({
            title,
            authors: author ? [author] : [],
            isbn13: isbn13 || null,
            isbn10: isbn10 || null,
            cover_url: coverUrl || null,
            source: 'openlibrary',
            language: 'en',
            enrichment_status: 'pending',
          })
          .select('id')
          .single();

        if (insertError) throw insertError;
        targetBookId = created.id;
      }

      // Open review dialog
      setReviewBookId(targetBookId);
      setReviewDialogOpen(true);
    } catch (err) {
      console.error('Error preparing book for review', err);
      toast.error('Unable to open review dialog', {
        description: 'Please try again in a moment.',
      });
    }
  };

  const handleReviewSubmit = async (data: {
    rating: number;
    reviewText: string;
    finishedAt?: Date;
    startedAt?: Date;
    tags?: string[];
    reading_format?: ReadingFormat;
  }) => {
    if (!reviewBookId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use centralized review submit - creates activity automatically
      const result = await submitReview(
        {
          rating: data.rating,
          reviewText: data.reviewText,
          finishedAt: data.finishedAt,
          startedAt: data.startedAt,
          tags: data.tags,
          reading_format: data.reading_format,
        },
        {
          userId: user.id,
          bookId: reviewBookId,
          bookTitle: title,
          bookCoverUrl: coverUrl,
          shelf: 'read',
          privacy: 'public',
        }
      );

      if (!result.success) {
        throw result.error || new Error('Failed to save review');
      }

      // Log positive feedback
      await submitFeedback({
        bookId: reviewBookId,
        bookTitle: title,
        bookAuthor: author,
        isbn13,
        isbn10,
        feedbackType: 'upvote',
        recommendationReason: reason,
      });

      toast.success('Review saved!');
      setReviewDialogOpen(false);
      setDismissed(true);
    } catch (err) {
      console.error('Error saving review:', err);
      toast.error('Failed to save review', {
        description: 'Please try again.',
      });
    }
  };

  // Shared details content for both hover card and sheet
  // Reordered: Main reason first, then history context, then source books
  const DetailsContent = () => (
    <div className="space-y-4 w-full">
      {/* Main recommendation reason - show first for better UX */}
      {reason && (
        <div className="flex gap-2">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-sm font-medium">Why we recommend this</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {reason}
            </p>
          </div>
        </div>
      )}
      
      {/* Match details - based on reading history */}
      {matchDetails && (
        <div className="flex gap-2 pt-3 border-t">
          <TrendingUp className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-sm font-medium">Based on your history</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {matchDetails}
            </p>
          </div>
        </div>
      )}

      {/* Source books - specific titles referenced */}
      {sourceBooks && sourceBooks.length > 0 && (
        <div className="flex gap-2 pt-3 border-t">
          <BookOpen className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-sm font-medium">Because you read</p>
            {sourceBooks.slice(0, 2).map((source, idx) => (
              <p key={idx} className="text-sm text-muted-foreground leading-relaxed">
                <span className="font-medium">{source.title}</span>
                {source.reason && <span className="block mt-0.5 text-xs">{source.reason}</span>}
              </p>
            ))}
          </div>
        </div>
      )}
      
      {/* Confidence score */}
      {confidenceScore !== undefined && (
        <div className="pt-3 border-t">
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-muted-foreground">Match confidence</span>
            <span className="font-medium">{confidenceScore}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all"
              style={{ width: `${confidenceScore}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const hasDetails = reason || matchDetails || sourceBooks;

  // Shared card content - cover and title now navigate to book, button opens reasoning
  const CardContent = ({ showWhyButton = true }: { showWhyButton?: boolean }) => (
    <>
      <div 
        className={cn(
          "relative overflow-hidden rounded-lg bg-muted cursor-pointer",
          compact ? "aspect-[2/3] w-16 flex-shrink-0" : "aspect-[2/3] w-full mb-3"
        )}
        onClick={handleViewDetails}
      >
        {coverUrl || bookId || isbn13 || isbn10 ? (
          <CoverImage
            bookId={bookId}
            isbn13={isbn13}
            isbn10={isbn10}
            title={title}
            author={author}
            coverUrl={coverUrl}
            size={compact ? "xs" : "sm"}
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className={cn("text-muted-foreground", compact ? "w-5 h-5" : "w-8 h-8")} />
          </div>
        )}
        
        {!compact && confidenceScore !== undefined && (
          <Badge 
            variant={confidenceScore >= 80 ? "default" : confidenceScore >= 60 ? "secondary" : "outline"}
            className="absolute top-2 right-2 text-xs"
          >
            {confidenceScore}% match
          </Badge>
        )}
        
        {!compact && recommendationType === 'wildcard' && (
          <Badge 
            variant="outline" 
            className="absolute bottom-2 left-2 text-xs bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-700"
          >
            🎲 Wild Card
          </Badge>
        )}
        
        {/* Info button overlay on cover - opens reasoning */}
        {!compact && hasDetails && showWhyButton && (
          <Button
            size="icon"
            variant="secondary"
            className="absolute top-2 left-2 h-7 w-7 rounded-full bg-background/80 backdrop-blur-sm hover:bg-background shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              setReasoningDialogOpen(true);
            }}
          >
            <HelpCircle className="w-4 h-4" />
          </Button>
        )}
      </div>
      
      <div className={cn("flex flex-col min-w-0", compact ? "flex-1" : "flex-1")}>
        <h3 
          className={cn(
            "font-medium group-hover:text-primary transition-colors leading-tight cursor-pointer",
            compact ? "text-xs line-clamp-2" : "text-sm line-clamp-2 mb-1 h-10"
          )}
          onClick={handleViewDetails}
        >
          {title}
        </h3>
        
        <p className={cn(
          "text-muted-foreground line-clamp-1",
          compact ? "text-xs" : "text-xs mb-2"
        )}>
          {author}
        </p>

        {/* Why this book button - always visible when we have details */}
        {!compact && hasDetails && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setReasoningDialogOpen(true);
            }}
            className="w-full text-xs text-primary hover:text-primary hover:bg-primary/10 mb-2 justify-start"
          >
            <Sparkles className="w-3 h-3 mr-1.5" />
            Why this book?
          </Button>
        )}

        {compact && reason && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {reason}
          </p>
        )}

        {!compact && (
          <div className="mt-auto space-y-2">
            {isInLibrary && libraryShelf === 'read' ? (
              <Button
                size="sm"
                variant="outline"
                disabled
                className="w-full text-xs"
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Already Read
              </Button>
            ) : isInLibrary && libraryShelf === 'reading' ? (
              <Button
                size="sm"
                variant="outline"
                disabled
                className="w-full text-xs"
              >
                <BookOpen className="w-3 h-3 mr-1" />
                Currently Reading
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  variant={bookIsSaved ? "outline" : "default"}
                  onClick={handleAddToTBR}
                  disabled={isSaving || bookIsSaved}
                  className="w-full text-xs"
                >
                  {bookIsSaved ? 'In TBR' : 'Add to TBR'}
                </Button>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAlreadyRead}
                  className="w-full text-xs"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Already Read
                </Button>
              </>
            )}
            
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              disabled={feedbackLoading}
              className="w-full text-xs text-muted-foreground hover:text-foreground"
            >
              Not interested
            </Button>
          </div>
        )}

        {compact && (
          <div className="flex justify-end mt-auto pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              disabled={feedbackLoading}
              className="h-7 px-2 text-xs text-muted-foreground"
            >
              ✕
            </Button>
          </div>
        )}
      </div>
    </>
  );

  // For compact mode, use a simple horizontal layout
  if (compact) {
    return (
      <div className="flex items-start gap-3 w-full">
        <CardContent showWhyButton={false} />
      </div>
    );
  }

  // Reasoning Dialog - works on both mobile and desktop
  const ReasoningDialog = () => (
    <Dialog open={reasoningDialogOpen} onOpenChange={setReasoningDialogOpen}>
      <DialogContent className="max-w-md mx-auto max-h-[85vh] flex flex-col">
        <DialogHeader className="pb-3 border-b flex-shrink-0">
          <DialogTitle className="text-left pr-8 line-clamp-2 text-lg">{title}</DialogTitle>
          <p className="text-sm text-muted-foreground text-left">by {author}</p>
        </DialogHeader>
        
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          <div className="space-y-4">
            <DetailsContent />
          </div>
        </div>
        
        {/* Fixed action buttons at bottom */}
        <div className="pt-4 border-t space-y-3 flex-shrink-0">
          <Button
            onClick={() => {
              setReasoningDialogOpen(false);
              handleViewDetails();
            }}
            className="w-full h-11"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            View Book Details
          </Button>
          
          {!isInLibrary && !bookIsSaved && (
            <Button
              variant="outline"
              onClick={(e) => {
                handleAddToTBR(e);
                setReasoningDialogOpen(false);
              }}
              disabled={isSaving}
              className="w-full h-11"
            >
              Add to TBR
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {/* Card - clicking cover/title goes to book, button opens reasoning */}
      <div className="group flex flex-col h-full">
        <CardContent />
      </div>

      {/* Reasoning Dialog */}
      <ReasoningDialog />

      {/* Review Dialog */}
      {reviewBookId && (
        <QuickReviewDialog
          isOpen={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          book={{
            id: reviewBookId,
            title,
            author,
          }}
          onSubmit={handleReviewSubmit}
        />
      )}
    </>
  );
}
